import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { createWebhook, updateWebhook } from '../../services/webhookService';
import { Webhook, WEBHOOK_OPERATIONS } from '../../types/webhook';
import { Loader2, Zap } from 'lucide-react';

interface CreateWebhookDialogProps {
  webhook?: Webhook; // If provided, dialog is in edit mode
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

export const CreateWebhookDialog: React.FC<CreateWebhookDialogProps> = ({
  webhook,
  onSuccess,
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [targetUrl, setTargetUrl] = useState(webhook?.targetUrl || '');
  const [operations, setOperations] = useState<string[]>(webhook?.operations || []);
  const { toast } = useToast();

  const isEditMode = !!webhook;

  const handleToggleOperation = (operation: string) => {
    setOperations((prev) =>
      prev.includes(operation)
        ? prev.filter((op) => op !== operation)
        : [...prev, operation]
    );
  };

  const validateUrl = (url: string): boolean => {
    try {
      const parsedUrl = new URL(url);
      // In production, should be HTTPS
      if (import.meta.env.PROD && parsedUrl.protocol !== 'https:') {
        toast({
          title: 'Validation Error',
          description: 'Webhook URL must use HTTPS in production',
          variant: 'destructive',
        });
        return false;
      }
      return true;
    } catch {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid URL',
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!targetUrl.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a webhook URL',
        variant: 'destructive',
      });
      return;
    }

    if (!validateUrl(targetUrl.trim())) {
      return;
    }

    if (operations.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one operation',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        await updateWebhook(webhook.id, {
          targetUrl: targetUrl.trim(),
          operations,
        });
        toast({
          title: 'Webhook Updated',
          description: 'Your webhook has been updated successfully',
        });
      } else {
        await createWebhook({
          targetUrl: targetUrl.trim(),
          operations,
        });
        toast({
          title: 'Webhook Created',
          description: 'Your webhook has been created successfully',
        });
      }

      setOpen(false);
      setTargetUrl('');
      setOperations([]);
      onSuccess();
    } catch (error) {
      console.error('Error saving webhook:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save webhook',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (!isEditMode) {
      setTargetUrl('');
      setOperations([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        handleClose();
      } else {
        setOpen(true);
      }
    }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Zap className="w-4 h-4 mr-2" />
            {isEditMode ? 'Edit Webhook' : 'Create Webhook'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Webhook' : 'Create New Webhook'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="url">Webhook URL</Label>
            <Input
              id="url"
              type="url"
              placeholder="https://example.com/webhook"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              The URL where webhook events will be sent
            </p>
          </div>

          <div className="space-y-3">
            <Label>Operations</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Select which operations should trigger this webhook
            </p>
            <div className="space-y-2">
              {WEBHOOK_OPERATIONS.map((op) => (
                <div key={op.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`op-${op.value}`}
                    checked={operations.includes(op.value)}
                    onCheckedChange={() => handleToggleOperation(op.value)}
                    disabled={loading}
                  />
                  <Label
                    htmlFor={`op-${op.value}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {op.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditMode ? 'Update Webhook' : 'Create Webhook'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
